import { Injectable } from '@angular/core';
import { MachineApiService } from './machine-api.service';

type ApiResponse<T> = { data: T; success: boolean };

export type GitHubInstallUrlResponse = { url: string; setupUrlHint?: string };
export type GitHubDefaultInstallationResponse = { installationId: number; accountLogin: string };
export type GitHubAppInstallationSummary = { installationId: number; accountLogin: string; accountType: 'User' | 'Organization' | 'Unknown' };

export type GitHubRepoSummary = {
  id: number;
  fullName: string;
  name: string;
  owner: string;
  private: boolean;
  defaultBranch: string;
  description?: string;
  url: string;
  updatedAt?: string;
};

export type GitHubRepoCreateResponse = GitHubRepoSummary & {
  installationId: number;
  installationMode: 'business' | 'personal';
  accountType?: 'User' | 'Organization' | 'Unknown';
};

@Injectable({ providedIn: 'root' })
export class ReposGitHubService {
  constructor(private api: MachineApiService) {}

  private async unwrap<T>(p: Promise<ApiResponse<T>>): Promise<T> {
    const res = await p;
    return res.data;
  }

  async getInstallUrl(): Promise<GitHubInstallUrlResponse> {
    return this.unwrap(this.api.request<ApiResponse<GitHubInstallUrlResponse>>('/api/github/app/install-url'));
  }

  async getDefaultInstallation(target: 'business' | 'personal' = 'personal'): Promise<GitHubDefaultInstallationResponse> {
    return this.unwrap(
      this.api.request<ApiResponse<GitHubDefaultInstallationResponse>>(
        `/api/github/app/default-installation?installationMode=${encodeURIComponent(target)}`
      )
    );
  }

  async listInstallations(): Promise<GitHubAppInstallationSummary[]> {
    const res = await this.unwrap(this.api.request<ApiResponse<{ installations: GitHubAppInstallationSummary[] }>>('/api/github/app/installations'));
    return Array.isArray((res as any)?.installations) ? (res as any).installations : [];
  }

  async getInstallationSuggestions(): Promise<{ business?: GitHubAppInstallationSummary; personal?: GitHubAppInstallationSummary; installations: GitHubAppInstallationSummary[] }> {
    return this.unwrap(this.api.request<ApiResponse<any>>('/api/github/app/installation-suggestions'));
  }

  async listRepos(installationId: number): Promise<{ totalCount: number; repositories: GitHubRepoSummary[] }> {
    return this.unwrap(
      this.api.request<ApiResponse<{ totalCount: number; repositories: GitHubRepoSummary[] }>>(
        `/api/github/repos?installationId=${encodeURIComponent(String(installationId))}`
      )
    );
  }

  async getRepo(installationId: number, owner: string, repo: string): Promise<any> {
    return this.unwrap(
      this.api.request<ApiResponse<any>>(
        `/api/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}?installationId=${encodeURIComponent(String(installationId))}`
      )
    );
  }

  async listPulls(
    installationId: number,
    owner: string,
    repo: string,
    state: 'open' | 'closed' | 'all' = 'open'
  ): Promise<any[]> {
    return this.unwrap(
      this.api.request<ApiResponse<any[]>>(
        `/api/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/pulls?installationId=${encodeURIComponent(
          String(installationId)
        )}&state=${encodeURIComponent(state)}`
      )
    );
  }

  async listCommits(installationId: number, owner: string, repo: string): Promise<any[]> {
    return this.unwrap(
      this.api.request<ApiResponse<any[]>>(
        `/api/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/commits?installationId=${encodeURIComponent(
          String(installationId)
        )}`
      )
    );
  }

  async listBranches(installationId: number, owner: string, repo: string): Promise<any[]> {
    return this.unwrap(
      this.api.request<ApiResponse<any[]>>(
        `/api/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/branches?installationId=${encodeURIComponent(
          String(installationId)
        )}`
      )
    );
  }

  async getStatus(
    installationId: number,
    owner: string,
    repo: string,
    ref?: string
  ): Promise<any> {
    const qs = new URLSearchParams({ installationId: String(installationId) });
    if (ref) qs.set('ref', ref);
    return this.unwrap(
      this.api.request<ApiResponse<any>>(`/api/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/status?${qs.toString()}`)
    );
  }

  async createRepo(input: {
    installationId: number;
    owner: string;
    name: string;
    description?: string;
    private?: boolean;
  }): Promise<GitHubRepoCreateResponse> {
    return this.unwrap(this.api.postJson<ApiResponse<GitHubRepoCreateResponse>>('/api/github/repos', input));
  }

  async upsertFiles(input: {
    installationId: number;
    owner: string;
    repo: string;
    branch?: string;
    message: string;
    files: Array<{ path: string; contentText?: string }>;
  }): Promise<any> {
    const { installationId, owner, repo, ...body } = input;
    return this.unwrap(
      this.api.postJson<ApiResponse<any>>(`/api/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/contents`, {
        installationId,
        ...body,
      })
    );
  }

  async createBranch(input: {
    installationId: number;
    owner: string;
    repo: string;
    baseBranch: string;
    name: string;
  }): Promise<any> {
    const { installationId, owner, repo, ...body } = input;
    return this.unwrap(
      this.api.postJson<ApiResponse<any>>(`/api/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/branches`, {
        installationId,
        ...body,
      })
    );
  }

  async createPull(input: {
    installationId: number;
    owner: string;
    repo: string;
    title: string;
    head: string;
    base: string;
    body?: string;
    draft?: boolean;
  }): Promise<any> {
    const { installationId, owner, repo, ...body2 } = input;
    return this.unwrap(
      this.api.postJson<ApiResponse<any>>(`/api/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/pulls`, {
        installationId,
        ...body2,
      })
    );
  }
}

